import themeColors, { themeFonts, themeFontsSize, themeFontWeights } from "@GeriMed/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    title = "title",
    description = "description",
    placeholder = "placeholder",
    disclaimer = "disclaimer",
    alignment = "alignment",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.description]: string;
        [fields.placeholder]: string;
        [fields.disclaimer]: string;
        [fields.alignment]: string;
        [fields.customCSS]: string;
    };
}

const mapDispatchToProps = {
    subscribe,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerGridItem?: GridItemProps;
    disclaimerText?: TypographyPresentationProps;
}

export const subscribeStyles: SubscribeStyles = {
    mainGridContainer: {
        gap: 10,
        css: css`
            width: 100%;
            display: flex;
            padding-bottom: 0 !important;
            padding: 0 15px;

            [class*="GridOffset"] {
                padding-top: 0 !important;
            }
            .description-grid-item {
                padding-left: 0;
            }
            .submitted-message {
                background-color: ${themeColors.dodgerBlue};
                font-size: ${themeFontsSize.size14};
                line-height: ${themeFontsSize.size24};
                font-weight: 500;
                padding: 5px 15px;
            }
            .user-error {
                line-height: 1.5em;
                font-weight: ${themeFontWeights.semiBold};
                display: block;
                clear: both;
                font-size: ${themeFontsSize.size14};
                margin-bottom: .25em;
                border: 1px solid ${themeColors.darkRed};
                padding: .25em 1em;
                background: ${themeColors.darkRed};
                color: ${themeColors.colorWhite};
                width:100%;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding: 0 30px;
                            .email-grid-item {
                                display: block;

                                > div {
                                    max-width: 100%;
                                    margin-bottom: 14px;
                                }
                            }
                            .user-error {
                                margin-top: 10px;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleLabel: {
        variant: "h4",
        css: css`
            margin-bottom: -5px;
        `,
    },
    descriptionGridItem: {
        width: 12,
    },
    descriptionText: {
        css: css`
            margin-bottom: -1rem;
        `,
    },
    emailGridItem: {
        width: 12,
        css: css`
            padding: 8px 0;
            align-items: baseline;
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                // max-width: 250px;
                color: ${themeColors.lightBlack};
                margin-right: 5px;
            `,
        },
    },
    emailButton: {
        css: css`
            padding: 0;
            min-height: 32px;
            span {
                padding: 8px;
                font-size: ${themeFontsSize.size12};
                line-height: ${themeFontsSize.size12};
            }
        `,
    },
    disclaimerGridItem: {
        width: 12,
    },
    disclaimerText: {
        css: css`
            font-size: ${themeFontsSize.size12};
        `,
    },
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
    border: none !important;
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

export const CmsSubscribe = ({ fields, subscribe, id }: Props) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const [isShow, setIsShow] = useState(true);
    const [isMessageShow, setIsMessageShow] = useState(false);

    const styles = useMergeStyles("subscribe", subscribeStyles);

    const Buttonhide = React.useCallback(() => {
        setIsShow(false);
    }, []);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!checkEmail(email)) {
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                setIsMessageShow(true);
                setIsShow(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        checkEmail(email);
        setEmail(email);
    };

    const checkEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const gridItemCss = (gridItemProps?: GridItemProps) => {
        return css`
            justify-content: ${fields.alignment ? fields.alignment : "center"};
            ${gridItemProps?.css}
        `;
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
            .input-container {
                position: relative;
                display: flex;
                align-items: center;
                background: white;
                border-radius: 20px;
                padding: 0 10px;
            }
            .email-text-field {
                border: none;
                outline: none;
                padding: 10px;
                border-radius: 25px;
                width: 200px;
            }
            .email-button {
                background: none;
                border: none !important;
                outline: none;
                border-radius: 50%;
                width: 34px;
                height: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                cursor: pointer;
                margin-left: 5px;
                img{
                    width: 40px;
                }
                &:hover{
                    background: none;
                }
            }
            .email-button::before {
                content: "\\2192";
                color: white;
                font-size: 16px;
            }
            div[class*="GridItemStyle"].email-grid-item{
                margin: 24px 0;
            }
        `,
    };

    return (
        <StyledWrapper className="get-mail-wrap" {...customCssWrapper}>
            <GridContainer className="main-grid-container" {...styles.mainGridContainer}>
                {/* <GridItem className="title-grid-item" {...styles.titleGridItem} css={gridItemCss(styles.titleGridItem)}>
                    <Typography className="title" {...styles.titleLabel}>
                        {fields.title}
                    </Typography>
                </GridItem> */}
                {isMessageShow === false && (
                    <GridItem
                        className="description-grid-item"
                        {...styles.descriptionGridItem}
                        css={gridItemCss(styles.descriptionGridItem)}
                    >
                        <Typography className="description-text" {...styles.descriptionText}>
                            {parse(fields.description, parserOptions)}
                        </Typography>
                    </GridItem>
                )}
                {isMessageShow && (
                    <Typography className="submitted-message">{siteMessage("Email_Subscribe_Success")}</Typography>
                )}
                <GridItem className="email-grid-item" {...styles.emailGridItem} css={gridItemCss(styles.emailGridItem)}>
                <div className="input-container">
                    <TextField
                        className="email-text-field"
                        {...styles.emailTextField}
                        id={id}
                        placeholder={fields.placeholder}
                        value={email}
                        onChange={e => emailChangeHandler(e.currentTarget.value)}
                    />
                    {isShow && (
                        <Button
                            className="email-button"
                            {...styles.emailButton}
                            onClick={onSubscribeClick}
                        >
                            {/* {fields.label} */}
                            <img src="/userfiles/orange-arrow.png" alt="Email Address" />
                        </Button>
                    )}
                    </div>
                </GridItem>
                {isSubmitted && emailError && (
                        <div className="user-error">
                            <span>{emailError}</span>
                        </div>
                    )}
            </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.alignment,
                displayName: "Layout",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.placeholder,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Enter your email",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.disclaimer,
                displayName: "Disclaimer",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
